import { useState } from "react";
import { useLocalization } from "@fluent/react";
import { Button, Select, TextInput } from "@narmi/design_system";
import {
  cents,
  currencyToCents,
  entities,
  useBusinessPermissions,
  useFeature,
  PERMISSION
} from "byzantine";
import type { AccountClassType } from "byzantine/src/Account";
import filters from "byzantine/src/filters";

import BorderCard from "../../BorderCard";
import styles from "./QuickTransferCard.module.scss";

const getAccountOptionLabel = (account: AccountClassType): string => {
  const { name, endingValue } = account.getTruncatedValuesWithoutBalance();
  return endingValue ? `${name} ${endingValue}` : name;
};

const QuickTransferCard = () => {
  const { l10n } = useLocalization();
  const { featureEnabled } = useFeature();
  const institution = entities.institution.useInstitution();

  const [fromAccountId, setFromAccountId] = useState<API.AccountId | "">("");
  const [toAccountId, setToAccountId] = useState<API.AccountId | "">("");
  const [amountCents, setAmountCents] = useState<number>(0);

  const [canTransferInternally] = useBusinessPermissions([
    PERMISSION.BUSINESS.WRITE.INTRA_INSTITUTION_TRANSFER
  ]);

  const sourceAccounts = entities.accounts
    .useValidTransferSources(undefined, featureEnabled("internal_transfers"))
    .filter((a) => a.isInternal());

  const destinationAccounts = entities.accounts
    .useValidTransferDestinations(
      !!institution?.ach_allows_push,
      undefined,
      featureEnabled("internal_transfers"),
      featureEnabled("enable_loans_in_transfer_flow")
    )
    .filter((a) => a.isInternal());

  const sourceAccountIds = new Set<string>(sourceAccounts.map((a) => a.id));
  const destinationAccountIds = new Set<string>(
    destinationAccounts.map((a) => a.id)
  );

  const isValid =
    sourceAccountIds.has(fromAccountId) &&
    destinationAccountIds.has(toAccountId) &&
    fromAccountId !== toAccountId &&
    amountCents > 0;

  const handleFromChange = (value: string) => {
    const nextFromAccountId = value as API.AccountId | "";
    if (nextFromAccountId && nextFromAccountId === toAccountId) {
      setToAccountId(
        destinationAccountIds.has(fromAccountId) ? fromAccountId : ""
      );
    }
    setFromAccountId(nextFromAccountId);
  };

  const handleToChange = (value: string) => {
    const nextToAccountId = value as API.AccountId | "";
    if (nextToAccountId && nextToAccountId === fromAccountId) {
      setFromAccountId(sourceAccountIds.has(toAccountId) ? toAccountId : "");
    }
    setToAccountId(nextToAccountId);
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAmountCents(currencyToCents(e.target.value));
  };

  const handleTransfer = () => {
    if (!isValid) return;
    const params = new URLSearchParams({
      from_account_id: fromAccountId,
      to_account_id: toAccountId,
      amount: String(amountCents),
      from_quick_transfer: "true"
    });
    window.location.href = `/transfer/funds/review?${params.toString()}`;
  };

  if (!featureEnabled("internal_transfers") || !canTransferInternally) {
    return null;
  }

  if (sourceAccounts.length === 0 || destinationAccounts.length === 0) {
    return null;
  }

  return (
    <BorderCard title={l10n.getString("heading-quick-transfer")}>
      <div className={styles.fields}>
        <div className={styles.amountInput}>
          <span className={styles.amountLabel}>
            {l10n.getString("label-amount")}
          </span>
          <TextInput
            label="$0.00"
            aria-label={l10n.getString("label-amount")}
            value={amountCents > 0 ? filters.currency(cents(amountCents)) : ""}
            onChange={handleAmountChange}
          />
        </div>
        <div className={styles.selectGroup}>
          <span className={styles.selectLabel}>
            {l10n.getString("label-from")}
          </span>
          <Select
            label={l10n.getString("quick-transfer-label-select-account")}
            aria-label={l10n.getString("label-from-account")}
            value={fromAccountId}
            onChange={handleFromChange}
          >
            {sourceAccounts.map((account) => (
              <Select.Item key={account.id} value={account.id}>
                {getAccountOptionLabel(account)}
              </Select.Item>
            ))}
          </Select>
        </div>
        <div className={styles.selectGroup}>
          <span className={styles.selectLabel}>
            {l10n.getString("label-to")}
          </span>
          <Select
            label={l10n.getString("quick-transfer-label-select-account")}
            aria-label={l10n.getString("label-to-account")}
            value={toAccountId}
            onChange={handleToChange}
          >
            {destinationAccounts.map((account) => (
              <Select.Item key={account.id} value={account.id}>
                {getAccountOptionLabel(account)}
              </Select.Item>
            ))}
          </Select>
        </div>
        <div className={styles.buttonContainer}>
          <Button
            endIcon="arrow-right"
            kind="primary"
            type="button"
            label={l10n.getString("button-quick-transfer")}
            onClick={handleTransfer}
            disabled={!isValid}
          />
        </div>
      </div>
    </BorderCard>
  );
};

export default QuickTransferCard;
